import { useCallback, useEffect, useRef, useState } from "react";
import { fetchById } from "./actions";
import { getLastMinuteRequestsUrl } from "./urls";

// Staff have ~10 minutes to answer a last-minute request from the public
// page, so a badge nobody happens to look at isn't enough. This watches the
// pending list on every admin page (and the reception kiosk) and makes noise:
// a sound + desktop notification when a request arrives, a reminder every
// minute until someone deals with it, and a blinking tab title.

const SOUND_KEY = "cyt_lm_sound";
const POLL_MS = 5000;
const REMIND_MS = 60000;

export const isLmSoundOn = () => {
  try { return localStorage.getItem(SOUND_KEY) !== "0"; } catch { return true; }
};
export const setLmSoundOn = (on) => {
  try { localStorage.setItem(SOUND_KEY, on ? "1" : "0"); } catch { /* private mode */ }
};

let audioCtx = null;
function getAudioCtx() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  return audioCtx;
}

// Two rising double-beeps — deliberately more insistent than the soft chime
// used for new leads, so it isn't mistaken for one.
export function playUrgentBeep() {
  if (!isLmSoundOn()) return;
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    [0, 0.22, 0.6, 0.82].forEach((t, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = i % 2 === 0 ? 880 : 1175;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.18);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + 0.2);
    });
  } catch { /* audio blocked — the banner and title blink still show */ }
}

export const desktopAlertsSupported = () => typeof window !== "undefined" && "Notification" in window;
export const desktopAlertsPermission = () => (desktopAlertsSupported() ? Notification.permission : "unsupported");

export async function requestDesktopAlerts() {
  if (!desktopAlertsSupported()) return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

function showDesktopNotification(fresh) {
  if (!desktopAlertsSupported() || Notification.permission !== "granted") return;
  try {
    const first = fresh[0];
    const n = new Notification(
      fresh.length > 1 ? `${fresh.length} last-minute requests waiting` : "Last-minute request waiting",
      {
        body: `${first.name} wants ${first.slot}${fresh.length > 1 ? ` (+${fresh.length - 1} more)` : ""} — accept within 10 minutes.`,
        tag: "cyt-last-minute", // a newer one replaces the old, but still pops up again
        renotify: true,
        requireInteraction: true,
      }
    );
    n.onclick = () => {
      window.focus();
      window.location.assign("/noida-appointments?view=lastMinute");
      n.close();
    };
  } catch { /* some browsers only allow notifications from a service worker */ }
}

export function useLastMinuteAlerts(enabled) {
  const [pending, setPending] = useState([]);
  const [, bump] = useState(0);
  const seen = useRef(new Set());
  const acked = useRef(new Set());
  const lastNoise = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;

    const poll = async () => {
      try {
        const res = await fetchById(getLastMinuteRequestsUrl);
        if (!alive || !res?.status) return;
        const list = (res.data || []).filter((r) => r.status === "pending");
        setPending(list);

        const fresh = list.filter((r) => !seen.current.has(r._id));
        fresh.forEach((r) => seen.current.add(r._id));
        // Forget requests that are settled, so the sets don't grow forever.
        const live = new Set(list.map((r) => r._id));
        acked.current.forEach((id) => { if (!live.has(id)) acked.current.delete(id); });

        const now = Date.now();
        if (fresh.length) {
          playUrgentBeep();
          showDesktopNotification(fresh);
          lastNoise.current = now;
        } else if (list.some((r) => !acked.current.has(r._id)) && now - lastNoise.current > REMIND_MS) {
          playUrgentBeep();
          lastNoise.current = now;
        }
      } catch { /* transient — next poll retries */ }
    };

    poll();
    const iv = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [enabled]);

  const unacked = pending.filter((r) => !acked.current.has(r._id));

  // "I've seen these" — hides the banner and stops the reminder beeps for the
  // requests waiting right now; a brand-new request alerts again.
  const acknowledge = useCallback(() => {
    pending.forEach((r) => acked.current.add(r._id));
    bump((n) => n + 1);
  }, [pending]);

  const unackedCount = unacked.length;
  useEffect(() => {
    if (!unackedCount) return undefined;
    const original = document.title;
    let on = true;
    const t = setInterval(() => {
      document.title = on ? `⚡ ${unackedCount} last-minute request${unackedCount > 1 ? "s" : ""}!` : original;
      on = !on;
    }, 800);
    return () => { clearInterval(t); document.title = original; };
  }, [unackedCount]);

  return { pending, unacked, acknowledge };
}
